import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  setCurrentPage, 
  setSelectedExpense, 
  clearLastPayee 
} from "../../redux/admin/academyExpense.slice";
import { 
  fetchExpenses, 
  fetchExpenseCategories, 
  fetchLastPayeeByCategory, 
  addExpense, 
  updateExpense 
} from "../../redux/admin/academyExpense.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "sm-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "sm-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const AcademyExpenses = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const expenseState = useSelector((state) => state.admin?.academyExpense || {});
  const branchState = useSelector((state) => state.admin?.branches || {});

  const globalBranches = branchState.branches || branchState.branchList || branchState.data || [];

  const {
    expenses = [],
    categories = [],
    lastPayee = null,
    summary = { total_amount: 0 },
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    categoriesLoading = false,
    selectedExpense = null,
  } = expenseState;

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);

  /* =====================================================
     INITIAL FORM STATE
  ====================================================== */
  const initialFormState = {
    expense_ref: "",
    expense_category_id: "",
    academy_branch_id: "",
    branch_name: "",
    expense_title: "",
    payee_name: "",
    payee_phone: "",
    payee_type: "vendor",
    invoice_no: "",
    expense_date: "",
    amount: "0.00",
    quantity: "1.00",
    unit_cost: "0.00",
    payment_method: "cash",
    reference_no: "",
    external_reference_no: "",
    status: "completed",
    description: "",
    bill_file: null
  };

  const [formData, setFormData] = useState(initialFormState);

  /* =====================================================
     SEARCH DEBOUNCE EFFECT
  ====================================================== */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchExpenses({ 
      page: currentPage, 
      limit, 
      search: debouncedSearch, 
      expense_category_id: categoryFilter,
      academy_branch_id: branchFilter,
      status: statusFilter, 
      payment_method: paymentMethodFilter 
    }));
  }, [dispatch, currentPage, debouncedSearch, categoryFilter, branchFilter, statusFilter, paymentMethodFilter]);

  useEffect(() => {
    dispatch(fetchExpenseCategories("active"));
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
  }, [dispatch]);

  /* =====================================================
     AUTO-POPULATE PAYEE & TITLE ON CATEGORY CHANGE (ADD MODE)
  ====================================================== */
  useEffect(() => {
    if (!isEditMode && formData.expense_category_id) {
      dispatch(fetchLastPayeeByCategory(formData.expense_category_id))
        .unwrap()
        .then((res) => {
          if (res) {
            setFormData((prev) => ({
              ...prev,
              payee_name: res.payee_name || prev.payee_name,
              payee_phone: res.payee_phone || prev.payee_phone,
              payee_type: res.payee_type || prev.payee_type,
              payment_method: res.payment_method || prev.payment_method,
              expense_title: res.expense_title || prev.expense_title
            }));
          }
        })
        .catch(() => {});
    } else if (!isEditMode && !formData.expense_category_id) {
      dispatch(clearLastPayee());
    }
  }, [formData.expense_category_id, isEditMode, dispatch]);

  /* =====================================================
     HANDLERS & HELPER FUNCTIONS
  ====================================================== */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) dispatch(setCurrentPage(newPage));
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] || null }));
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "academy_branch_id") {
        const selectedBranch = globalBranches.find(b => String(b.id) === String(value));
        updated.branch_name = selectedBranch ? (selectedBranch.branch_name || selectedBranch.name) : "";
      }

      if (name === "amount" || name === "quantity") {
        const amt = parseFloat(name === "amount" ? value : updated.amount) || 0;
        const qty = parseFloat(name === "quantity" ? value : updated.quantity) || 1;
        if (qty > 0) {
          updated.unit_cost = (amt / qty).toFixed(2);
        }
      }

      return updated;
    });
  };

  const openAddModal = () => {
    setIsEditMode(false);
    dispatch(clearLastPayee());
    
    const currentYear = new Date().getFullYear();
    const nextSeq = String(total + 1).padStart(4, '0');
    const generatedRef = `EXP-${currentYear}-${nextSeq}`;

    setFormData({
      ...initialFormState,
      expense_ref: generatedRef,
      expense_date: new Date().toISOString().split('T')[0]
    });
    setActiveTab("general");
    setShowModal(true);
  };

  const openEditModal = (ex) => {
    setIsEditMode(true);
    dispatch(setSelectedExpense(ex));
    setFormData({ 
      ...initialFormState, 
      ...ex,
      expense_category_id: ex.expense_category_id || "",
      academy_branch_id: ex.academy_branch_id || "",
      branch_name: ex.branch_name || "",
      payee_type: ex.payee_type || "vendor",
      payment_method: ex.payment_method || "cash",
      status: ex.status || "completed",
      bill_file: ex.bill_file || null
    });
    setActiveTab("general");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.expense_category_id || !formData.expense_title || !formData.expense_date || !formData.amount) {
      toastr.warning("Expense Category, Title, Date, and Amount are mandatory");
      return;
    }

    setSaving(true);
    try {
      const dataForm = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          dataForm.append(key, formData[key]);
        }
      });

      if (isEditMode) {
        await dispatch(updateExpense({ id: selectedExpense.id, expenseData: dataForm })).unwrap();
        toastr.success("Expense Record Updated Successfully");
      } else {
        await dispatch(addExpense(dataForm)).unwrap();
        toastr.success("Expense Recorded Successfully");
      }
      setShowModal(false);
      dispatch(fetchExpenses({ page: currentPage, limit, search: debouncedSearch, expense_category_id: categoryFilter, academy_branch_id: branchFilter, status: statusFilter, payment_method: paymentMethodFilter }));
    } catch (err) { 
      toastr.error(err || "Process Failed"); 
    } finally {
      setSaving(false);
    }
  };

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => handlePageChange(i)}
        className={`sm-page-btn${currentPage === i ? " active" : ""}`}
      >
        {i}
      </button>
    ));

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="sm-page">
      <div className="sm-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="sm-header">
          <div>
            <p className="sm-eyebrow">Academy Operations · Admin Console</p>
            <h1 className="sm-title">Academy Expenses Management</h1>
          </div>
          <button type="button" className="sm-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> Record Expense
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="sm-kpi-grid">
          {[
            { label: "Total expenses recorded", value: total },
            { label: "Total amount outlay", value: `₹${parseFloat(summary.total_amount || 0).toLocaleString()}` },
            { label: "Current page", value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="sm-kpi">
              <span className="sm-kpi-label">{label}</span>
              <span className="sm-kpi-val">{loading ? <Skeleton w={50} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="sm-toolbar">
          <div className="sm-search">
            <i className="fas fa-search sm-search-icon"></i>
            <input
              type="text"
              placeholder="Search ref, title, payee, invoice no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="sm-filter-item">
            <select className="sm-field bg-white" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.category_name}</option>
              ))}
            </select>
          </div>
          <div className="sm-filter-item">
            <select className="sm-field bg-white" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="">All Branches</option>
              {globalBranches.map(b => (
                <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>
              ))}
            </select>
          </div>
          <div className="sm-filter-item">
            <select className="sm-field bg-white" value={paymentMethodFilter} onChange={(e) => setPaymentMethodFilter(e.target.value)}>
              <option value="">All Payment Modes</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="sm-filter-item">
            <select className="sm-field bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* ── DATA TABLE ── */}
        <div className="sm-card">
          <div className="table-responsive">
            <table className="sm-table">
              <thead>
                <tr>
                  <th style={{ width: "24%" }}>Expense Ref & Title</th>
                  <th style={{ width: "20%" }}>Category & Branch</th>
                  <th style={{ width: "20%" }}>Payee Details</th>
                  <th style={{ width: "16%" }}>Amount & Mode</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th className="text-end" style={{ width: "8%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="sm-row">
                      <td>
                        <div className="sm-name-cell">
                          <Skeleton w={34} h={34} r={8} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="65%" h={12} />
                            <Skeleton w="45%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td><Skeleton w={100} h={12} /></td>
                      <td><Skeleton w={90} h={12} /></td>
                      <td><Skeleton w={70} h={12} /></td>
                      <td><Skeleton w={60} h={22} r={20} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={50} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : expenses.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="sm-empty-state">
                      <div className="sm-empty-icon-wrap">
                        <i className="fas fa-receipt"></i>
                      </div>
                      <p className="sm-empty-title">No expense records found</p>
                      <span className="sm-empty-desc">Try modifying your filter or record a new expense.</span>
                    </td>
                  </tr>
                ) : (
                  expenses.map((ex) => (
                    <tr key={ex.id} className="sm-row">
                      <td>
                        <div className="sm-name-cell">
                          <div className="sm-avatar">{ex.expense_ref?.[0]?.toUpperCase() || "E"}</div>
                          <div>
                            <div className="sm-name">{ex.expense_title}</div>
                            <div className="sm-loc">{ex.expense_ref} · {ex.expense_date}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="sm-name">{ex.category_name || "General"}</div>
                        <div className="sm-loc">{ex.branch_name || "Main Branch"}</div>
                      </td>
                      <td>
                        <div className="sm-name">{ex.payee_name || "N/A"}</div>
                        <div className="sm-loc">{ex.payee_phone || "No phone"}</div>
                      </td>
                      <td>
                        <div className="sm-date-main">₹{parseFloat(ex.amount || 0).toLocaleString()}</div>
                        <div className="sm-loc text-uppercase fw-medium text-primary">{ex.payment_method}</div>
                      </td>
                      <td>
                        <span className={`sm-pill ${
                          ex.status === 'completed' ? 'pill-green' :
                          ex.status === 'pending' ? 'pill-amber' : 'pill-coral'
                        }`}>
                          {ex.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-end">
                        <button type="button" className="sm-btn-outline" onClick={() => openEditModal(ex)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          <div className="sm-footer">
            <span className="sm-count">{expenses.length} of {total} records</span>
            <div className="sm-pagination">
              <button type="button" className="sm-page-btn sm-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button type="button" className="sm-page-btn sm-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div
          className="sm-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}
        >
          <div className="sm-modal">

            {/* Sidebar */}
            <div className="sm-modal-side">
              <div className="sm-modal-side-header">
                <p className="sm-modal-side-title">Expense Wizard</p>
                <p className="sm-modal-side-sub">Outlay & Payee Ledger</p>
              </div>
              <nav className="sm-modal-nav">
                {[
                  { key: "general", icon: "fas fa-info-circle", label: "General & Category" },
                  { key: "payee", icon: "fas fa-user-tie", label: "Payee & Vendor" },
                  { key: "financial", icon: "fas fa-calculator", label: "Amount & Unit" },
                  { key: "payment", icon: "fas fa-wallet", label: "Payment & Mode" },
                  { key: "bill", icon: "fas fa-file-invoice", label: "Bill & Invoice" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`sm-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                  </button>
                ))}
              </nav>

              <div className="sm-modal-side-status">
                <div className="sm-status-toggle-row" style={{ background: "#fff" }}>
                  <div>
                    <label className="sm-label" style={{ marginBottom: 2 }}>Status</label>
                  </div>
                  <select name="status" className="sm-field" style={{ width: "auto", padding: "4px 8px", fontSize: "11.5px" }} value={formData.status} onChange={handleChange} disabled={saving}>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Main panel */}
            <form onSubmit={handleSubmit} className="sm-modal-main">
              <div className="sm-modal-header">
                <div>
                  <h2 className="sm-modal-title">{isEditMode ? "Update Expense Record" : "Record New Expense"}</h2>
                  <p className="sm-modal-sub">Ref: {formData.expense_ref || "PENDING"}</p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="sm-modal-body">
                <div className="row g-3">

                  {/* ── GENERAL & CATEGORY ── */}
                  {activeTab === "general" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Expense Ref *</label>
                        <input type="text" name="expense_ref" className="sm-field" placeholder="e.g. EXP-2026-0001" value={formData.expense_ref} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Expense Category *</label>
                        <select name="expense_category_id" className="sm-field" value={formData.expense_category_id} onChange={handleChange} disabled={categoriesLoading}>
                          <option value="">Select Category</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                          ))}
                        </select>
                      </div>

                      {/* ── LAST TRANSACTION / PAYEE INFO BANNER ── */}
                      {!isEditMode && lastPayee && (
                        <div className="col-12">
                          <div style={{ background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                            <div style={{ color: "#0071e3", fontSize: "16px", marginTop: "1px" }}>
                              <i className="fas fa-history"></i>
                            </div>
                            <div style={{ flex: 1, fontSize: "12.5px" }}>
                              <div style={{ fontWeight: 600, color: "#1e40af", marginBottom: "4px" }}>
                                Last Recorded Transaction for this Category
                              </div>
                              <div className="sm-last-payee-grid">
                                <div><span style={{ color: "#6e6e73" }}>Title:</span> <strong>{lastPayee.expense_title || "N/A"}</strong></div>
                                <div><span style={{ color: "#6e6e73" }}>Payee:</span> <strong>{lastPayee.payee_name || "N/A"}</strong></div>
                                <div><span style={{ color: "#6e6e73" }}>Amount:</span> <strong className="text-primary">₹{parseFloat(lastPayee.amount || 0).toLocaleString()}</strong></div>
                                <div><span style={{ color: "#6e6e73" }}>Date:</span> <strong>{lastPayee.expense_date || "N/A"}</strong></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="col-md-6">
                        <label className="sm-label">Academy Branch</label>
                        <select name="academy_branch_id" className="sm-field" value={formData.academy_branch_id} onChange={handleChange}>
                          <option value="">Select Branch</option>
                          {globalBranches.map(b => (
                            <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Expense Title / Particulars *</label>
                        <input type="text" name="expense_title" className="sm-field" placeholder="e.g. Monthly Electricity Bill" value={formData.expense_title} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Expense Date *</label>
                        <input type="date" name="expense_date" className="sm-field" value={formData.expense_date} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Invoice No</label>
                        <input type="text" name="invoice_no" className="sm-field" placeholder="e.g. INV-9982" value={formData.invoice_no} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {/* ── PAYEE & VENDOR ── */}
                  {activeTab === "payee" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Payee Type</label>
                        <select name="payee_type" className="sm-field" value={formData.payee_type} onChange={handleChange}>
                          <option value="vendor">Vendor</option>
                          <option value="person">Person</option>
                          <option value="organization">Organization</option>
                          <option value="service_provider">Service Provider</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Payee Name</label>
                        <input type="text" name="payee_name" className="sm-field" placeholder="e.g. TNEB Electricity Board" value={formData.payee_name} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Payee Phone Number</label>
                        <input type="text" name="payee_phone" className="sm-field" placeholder="e.g. 9876543210" value={formData.payee_phone} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {/* ── AMOUNT & UNIT ── */}
                  {activeTab === "financial" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Total Amount (₹) *</label>
                        <input type="number" step="0.01" name="amount" className="sm-field fw-bold text-primary" value={formData.amount} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Quantity</label>
                        <input type="number" step="0.01" name="quantity" className="sm-field" value={formData.quantity} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Unit Cost (₹)</label>
                        <input type="number" step="0.01" name="unit_cost" className="sm-field" value={formData.unit_cost} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {/* ── PAYMENT & MODE ── */}
                  {activeTab === "payment" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Payment Method</label>
                        <select name="payment_method" className="sm-field" value={formData.payment_method} onChange={handleChange}>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI / QR Code</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="card">Card</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Reference No / UTR</label>
                        <input type="text" name="reference_no" className="sm-field" placeholder="e.g. UTR123456789" value={formData.reference_no} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">External Reference No</label>
                        <input type="text" name="external_reference_no" className="sm-field" placeholder="External ref..." value={formData.external_reference_no} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="sm-label">Description / Remarks</label>
                        <textarea name="description" rows="3" className="sm-field" value={formData.description} onChange={handleChange} placeholder="Expense particulars..." />
                      </div>
                    </>
                  )}

                  {/* ── BILL & INVOICE FILE UPLOAD ── */}
                  {activeTab === "bill" && (
                    <div className="col-12">
                      <label className="sm-label">Upload Bill / Invoice Document (PDF, PNG, JPG - Max 5MB)</label>
                      
                      {isEditMode && typeof formData.bill_file === 'string' && formData.bill_file !== "" && (
                        <div style={{ marginBottom: "14px", padding: "10px 14px", background: "#f5f5f7", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e8e8ed" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px" }}>
                            <i className="fas fa-file-alt text-primary"></i>
                            <span>Existing Document Attached</span>
                          </div>
                          <a 
                            href={formData.bill_file} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="sm-btn-outline" 
                            style={{ textDecoration: "none", padding: "4px 10px", fontSize: "11.5px" }}
                          >
                            View / Download
                          </a>
                        </div>
                      )}

                      <input 
                        type="file" 
                        name="bill_file" 
                        accept=".pdf,image/png,image/jpeg" 
                        onChange={handleChange} 
                        className="sm-field" 
                        style={{ padding: "7px 12px" }}
                      />
                      <small style={{ color: "#6e6e73", fontSize: "11px", marginTop: "4px", display: "block" }}>
                        {isEditMode && typeof formData.bill_file === 'string' ? "Uploading a new file will replace the existing invoice document." : "Attach physical receipt or digital invoice scan."}
                      </small>
                    </div>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER with loader ── */}
              <div className="sm-modal-footer">
                <button type="button" className="sm-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Discard
                </button>
                <button type="submit" className="sm-btn-primary sm-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Updating…" : "Recording…"}
                    </>
                  ) : (
                    isEditMode ? "Update Expense" : "Save Expense"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes sm-spin { to { transform: rotate(360deg); } }
        @keyframes sm-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes sm-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes sm-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        /* ── BASE ── */
        .sm-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          color: #1d1d1f;
          font-size: 14px;
        }
        .sm-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        /* ── PAGE HEADER ── */
        .sm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .sm-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .sm-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        /* ── KPI STRIP ── */
        .sm-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        .sm-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .sm-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .sm-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        /* ── TOOLBAR ── */
        .sm-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; flex-wrap: wrap; }
        .sm-search { flex: 1; min-width: 220px; position: relative; }
        .sm-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .sm-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .sm-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .sm-search input::placeholder { color: #aeaeb2; }
        .sm-filter-item { min-width: 150px; flex-shrink: 0; }

        /* ── PRIMARY BUTTON ── */
        .sm-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: background 0.15s, opacity 0.15s; white-space: nowrap; font-family: inherit;
          min-width: 140px; justify-content: center;
        }
        .sm-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .sm-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .sm-btn-submit { padding: 9px 22px; min-width: 158px; }

        /* ── TABLE CARD ── */
        .sm-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .sm-table { width: 100%; border-collapse: collapse; }
        .sm-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px;
          border-bottom: 1px solid #e8e8ed; white-space: nowrap;
        }
        .sm-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .sm-table tbody tr:last-child { border-bottom: none; }
        .sm-row:hover { background: #fafafa; }
        .sm-table td { padding: 13px 18px; vertical-align: middle; }

        .sm-name-cell { display: flex; align-items: center; gap: 10px; }
        .sm-avatar {
          width: 34px; height: 34px; border-radius: 8px; background: #f5f5f7; border: 1px solid #e8e8ed;
          color: #3c3c43; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .sm-loc { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }

        /* ── PILLS ── */
        .sm-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .pill-coral { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        .sm-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        /* ── OUTLINE BUTTON ── */
        .sm-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 12px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .sm-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        /* ── EMPTY STATE ── */
        .sm-empty-state { text-align: center; padding: 3rem 1rem; }
        .sm-empty-icon-wrap {
          width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px;
        }
        .sm-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .sm-empty-desc { font-size: 12px; color: #aeaeb2; }

        /* ── PAGINATION ── */
        .sm-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; flex-wrap: wrap; gap: 10px; }
        .sm-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .sm-pagination { display: flex; gap: 4px; flex-wrap: wrap; }
        .sm-page-btn {
          min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff;
          border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; font-family: inherit;
        }
        .sm-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .sm-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .sm-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ── MODAL ── */
        .sm-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: sm-overlay-in 0.2s ease;
        }
        .sm-modal {
          width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7;
          box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06);
          display: flex; overflow: hidden; animation: sm-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1);
        }

        /* Sidebar */
        .sm-modal-side { width: 220px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .sm-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .sm-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .sm-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .sm-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .sm-nav-item {
          display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent;
          font-size: 13px; color: #6e6e73; font-weight: 400; cursor: pointer; text-align: left; width: 100%;
          transition: all 0.12s; position: relative; font-family: inherit;
        }
        .sm-nav-item i { font-size: 12.5px; width: 14px; text-align: center; flex-shrink: 0; }
        .sm-nav-item span { flex: 1; }
        .sm-nav-item:hover:not(.active):not(:disabled) { background: #ebebf0; color: #3c3c43; }
        .sm-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }
        .sm-nav-item:disabled { opacity: 0.5; cursor: not-allowed; }
        .sm-modal-side-status { padding: 16px; margin-top: auto; }

        /* Main panel */
        .sm-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .sm-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .sm-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; letter-spacing: -0.015em; }
        .sm-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .sm-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .sm-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        /* ── Form fields ── */
        .sm-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .sm-field {
          width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f;
          background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .sm-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .sm-field::placeholder { color: #aeaeb2; }
        select.sm-field { cursor: pointer; }

        .sm-status-toggle-row {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 12px 16px;
        }

        .sm-last-payee-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 6px; color: #3c3c43;
        }

        /* ── CANCEL BUTTON ── */
        .sm-btn-cancel {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .sm-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }
        .sm-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── MODAL BODY SCROLLBAR ── */
        .sm-modal-body::-webkit-scrollbar { width: 5px; }
        .sm-modal-body::-webkit-scrollbar-track { background: transparent; }
        .sm-modal-body::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }

        /* ── RESPONSIVE ADAPTATIONS (MOBILE OPTIMIZED) ── */
        @media (max-width: 992px) {
          .sm-kpi-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 768px) {
          .sm-wrap { padding: 1rem 0.75rem; }
          .sm-kpi-grid { grid-template-columns: 1fr; gap: 8px; }
          .sm-kpi { padding: 12px 14px; }
          .sm-kpi-val { font-size: 1.25rem; }
          .sm-toolbar { flex-direction: column; align-items: stretch; gap: 8px; }
          .sm-search { width: 100%; min-width: 0; }
          .sm-filter-item { width: 100%; min-width: 0; }
          .sm-filter-item select { width: 100%; }
          
          /* Modal Responsive Layout */
          .sm-modal-overlay { padding: 8px; align-items: flex-end; }
          .sm-modal { 
            flex-direction: column; 
            height: 85vh; 
            max-height: 85vh; 
            border-bottom-left-radius: 0; 
            border-bottom-right-radius: 0; 
          }
          .sm-modal-side { width: 100%; height: auto; border-right: none; border-bottom: 1px solid #e8e8ed; flex-shrink: 0; }
          .sm-modal-side-header { display: none; }
          .sm-modal-side-status { display: none; }
          .sm-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; scrollbar-width: none; gap: 4px; }
          .sm-modal-nav::-webkit-scrollbar { display: none; }
          .sm-nav-item { white-space: nowrap; width: auto; font-size: 11.5px; padding: 6px 10px; border-radius: 6px; }
          .sm-modal-body { padding: 14px; overflow-y: auto; }
          .sm-modal-footer { padding: 10px 14px; flex-wrap: nowrap; gap: 8px; }
          .sm-modal-header { padding: 12px 14px; }
          .sm-btn-submit, .sm-btn-cancel { flex: 1; min-width: 0; padding: 8px 12px; font-size: 12px; }
        }
        @media (max-width: 480px) {
          .sm-header { flex-direction: column; gap: 10px; }
          .sm-btn-primary { width: 100%; justify-content: center; }
          .sm-last-payee-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default AcademyExpenses;